import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Badge } from '../../../../../components/ui/badge';
import { useOps } from '../../../../../hooks/useOps';
import { formatCurrencyBrlFromCents, formatCpf, formatPhone, formatDateIso } from '../../../../../utils';
import { romaneioService } from '../../../../../api';
import type {
  IndriveFechamentoResumoRow,
  IndriveFechamentoOperacaoRow,
  IndriveFechamentoCampanhaRow,
  IndriveFechamentoResponse,
} from '../types';

const downloadCsv = (rows: Record<string, unknown>[], filename: string) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers.map((h) => {
        const v = String(r[h] ?? '');
        return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(',')
    ),
  ].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

async function fetchFechamento(ops: string, sexta?: string): Promise<IndriveFechamentoResponse> {
  const response = await romaneioService.getIndriveFechamento({
    ops,
    ...(sexta ? { sexta } : {}),
  });
  if (response.success) return response.data as unknown as IndriveFechamentoResponse;
  throw new Error(response.message || 'Erro ao carregar dados');
}

export function useIndriveFechamento() {
  const { currentOps } = useOps();
  const [selectedSexta, setSelectedSexta] = useState('');

  const query = useQuery({
    queryKey: ['indrive-fechamento', currentOps ?? '', selectedSexta],
    queryFn: () => fetchFechamento(currentOps!, selectedSexta || undefined),
    enabled: Boolean(currentOps),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const data = query.data ?? null;
  // Vencimento efetivo (o selecionado ou o mais recente devolvido pelo backend).
  const vencimento = selectedSexta || data?.periodo.sexta || '';
  const vencimentos = data?.vencimentos ?? [];
  const isOpenCycle = data?.isOpenCycle ?? false;

  const buildFilename = useCallback(
    (prefix: string) => {
      const suffix = isOpenCycle ? '_CICLO_EM_ABERTO' : '';
      return `${prefix}_${currentOps}_venc_${vencimento}${suffix}.csv`;
    },
    [currentOps, vencimento, isOpenCycle]
  );

  const handleDownloadResumo = useCallback(() => {
    if (!data) return;
    downloadCsv(
      data.resumo.map((r) => ({
        id_indrive: r.id_indrive ?? '',
        cpf: r.cpf,
        nome: r.nome ?? '',
        telefone: r.telefone,
        pagamentos: r.pagamentos,
        valor_face: r.valor_face,
        valor_compra: r.valor_compra,
      })),
      buildFilename('resumo_indrive')
    );
  }, [data, buildFilename]);

  const handleDownloadOperacoes = useCallback(() => {
    if (!data) return;
    downloadCsv(
      data.operacoes.map((r) => ({
        id_campanha: r.id_campanha,
        id_indrive: r.id_indrive ?? '',
        cpf: r.cpf,
        nome: r.nome ?? '',
        telefone: r.telefone,
        valor_face: r.valor_face,
        valor_compra: r.valor_compra,
        status: r.liquidado ? 'liquidado' : r.comprado ? 'comprado' : '',
        data_romaneio: r.data_romaneio?.substring(0, 10) ?? '',
        data_vencimento: r.data_vencimento?.substring(0, 10) ?? '',
      })),
      buildFilename('pagamentos_indrive')
    );
  }, [data, buildFilename]);

  const handleDownloadCampanhas = useCallback(() => {
    if (!data) return;
    downloadCsv(
      (data.porCampanha ?? []).map((r) => ({
        campanha: r.campanha,
        pagamentos: r.pagamentos,
        valor_face: r.valor_face,
        valor_compra: r.valor_compra,
      })),
      buildFilename('campanhas_indrive')
    );
  }, [data, buildFilename]);

  const resumoColumns: ColumnDef<IndriveFechamentoResumoRow>[] = useMemo(
    () => [
      {
        accessorKey: 'id_indrive',
        header: 'ID inDrive',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? <span className="font-mono text-sm">{v}</span> : <span className="text-[hsl(215,16%,70%)] italic text-sm">—</span>;
        },
      },
      {
        accessorKey: 'cpf',
        header: 'CPF',
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatCpf((getValue() as string).replace(/\D/g, ''))}</span>
        ),
      },
      {
        accessorKey: 'nome',
        header: 'Nome',
        size: 200,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? <span className="font-medium">{v}</span> : <span className="text-[hsl(215,16%,70%)] italic text-sm">—</span>;
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 150,
        cell: ({ getValue }) => <span className="font-mono text-sm">{formatPhone(getValue() as string)}</span>,
      },
      {
        accessorKey: 'pagamentos',
        header: 'Pagamentos',
        size: 110,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => <span className="tabular-nums">{getValue() as number}</span>,
      },
      {
        accessorKey: 'valor_face',
        header: 'Valor Face',
        size: 140,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-amber-700">{formatCurrencyBrlFromCents(getValue() as number)}</span>
        ),
      },
      {
        accessorKey: 'valor_compra',
        header: 'Valor Compra',
        size: 140,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-indigo-700">{formatCurrencyBrlFromCents(getValue() as number)}</span>
        ),
      },
    ],
    []
  );

  const operacoesColumns: ColumnDef<IndriveFechamentoOperacaoRow>[] = useMemo(
    () => [
      {
        accessorKey: 'id_campanha',
        header: 'Campanha',
        size: 150,
        cell: ({ getValue }) => {
          const v = getValue() as string;
          return v ? <span className="font-medium">{v}</span> : <span className="text-[hsl(215,16%,70%)] italic text-sm">—</span>;
        },
      },
      {
        accessorKey: 'id_indrive',
        header: 'ID inDrive',
        size: 130,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? <span className="font-mono text-sm">{v}</span> : <span className="text-[hsl(215,16%,70%)] italic text-sm">—</span>;
        },
      },
      {
        accessorKey: 'cpf',
        header: 'CPF',
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatCpf((getValue() as string).replace(/\D/g, ''))}</span>
        ),
      },
      {
        accessorKey: 'nome',
        header: 'Nome',
        size: 180,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? <span className="font-medium">{v}</span> : <span className="text-[hsl(215,16%,70%)] italic text-sm">—</span>;
        },
      },
      {
        accessorKey: 'valor_face',
        header: 'Valor Face',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-amber-700">{formatCurrencyBrlFromCents(getValue() as number)}</span>
        ),
      },
      {
        accessorKey: 'valor_compra',
        header: 'Valor Compra',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-indigo-700">{formatCurrencyBrlFromCents(getValue() as number)}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        size: 120,
        accessorFn: (row) => (row.liquidado ? 'liquidado' : row.comprado ? 'comprado' : '—'),
        cell: ({ row }) => {
          const r = row.original;
          if (r.liquidado) return <Badge className="bg-emerald-100 text-emerald-800">Liquidado</Badge>;
          if (r.comprado) return <Badge className="bg-indigo-100 text-indigo-800">Comprado</Badge>;
          return <span className="text-[hsl(215,16%,70%)] italic text-sm">—</span>;
        },
      },
      {
        accessorKey: 'data_vencimento',
        header: 'Vencimento',
        size: 120,
        cell: ({ getValue }) => <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>,
      },
    ],
    []
  );

  const campanhasColumns: ColumnDef<IndriveFechamentoCampanhaRow>[] = useMemo(
    () => [
      {
        accessorKey: 'campanha',
        header: 'Campanha',
        size: 200,
        cell: ({ getValue }) => {
          const v = getValue() as string;
          return v ? <span className="font-medium">{v}</span> : <span className="text-[hsl(215,16%,70%)] italic text-sm">—</span>;
        },
      },
      {
        accessorKey: 'pagamentos',
        header: 'Pagamentos',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => <span className="tabular-nums">{getValue() as number}</span>,
      },
      {
        accessorKey: 'valor_face',
        header: 'Valor Face',
        size: 150,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-amber-700">{formatCurrencyBrlFromCents(getValue() as number)}</span>
        ),
      },
      {
        accessorKey: 'valor_compra',
        header: 'Valor Compra',
        size: 150,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-indigo-700">{formatCurrencyBrlFromCents(getValue() as number)}</span>
        ),
      },
    ],
    []
  );

  const summaryStats = useMemo(() => {
    if (!data) return null;
    return {
      totalMotoristas: data.resumo.length,
      totalPagamentos: data.realizado?.total_count ?? 0,
      valorFaceTotal: data.realizado?.valor_face_total ?? 0,
      valorCompraTotal: data.realizado?.valor_compra_total ?? 0,
    };
  }, [data]);

  return {
    currentOps,
    data,
    summaryStats,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error?.message || 'Erro ao conectar com o servidor' : '',
    vencimentos,
    vencimento,
    selectedSexta,
    setSelectedSexta,
    isOpenCycle,
    resumoColumns,
    operacoesColumns,
    campanhasColumns,
    handleDownloadResumo,
    handleDownloadOperacoes,
    handleDownloadCampanhas,
    loadData: () => query.refetch(),
  };
}
